/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useRef } from 'react';
import { Calendar, Download } from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
  PieChart,
  Pie,
  Legend,
} from 'recharts';
import moment from 'moment';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import useGetPaymentsAnalytics from 'hooks/payments/useGetPaymentAnalytics';
import DashboardSkeleton from 'components/common/skeletons/DashboardSkeleton';
import { PaymentsAnalyticsResponse } from 'utils/types';
import { formatDateForInput } from 'utils/Functions';

// ─── Helpers ────────────────────────────────────────────────────────────────
const ksh = (n?: number | null) => `KSh ${(n ?? 0).toLocaleString('en-KE')}`;

const fmt = (n?: number | null) => (n ?? 0).toLocaleString('en-KE');

const pct = (part: number, total: number) =>
  total === 0 ? 0 : Math.round((part / total) * 100);

// ─── Sub-components ──────────────────────────────────────────────────────────
const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-3">
    {children}
  </p>
);

const MetricCard = ({
  label,
  value,
  sub,
  valueColor,
}: {
  label: string;
  value: string;
  sub: string;
  valueColor?: string;
}) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <p className="text-sm text-gray-500 mb-1">{label}</p>
    <p className={`text-xl font-semibold ${valueColor ?? 'text-gray-800'}`}>
      {value}
    </p>
    <p className="text-xs text-gray-400 mt-0.5">{sub}</p>
  </div>
);

const ProgressRow = ({
  label,
  amount,
  count,
  share,
  color,
}: {
  label: string;
  amount: number;
  count: number;
  share: number;
  color: string;
}) => (
  <div>
    <div className="flex justify-between text-sm mb-1">
      <span className="text-gray-500">{label}</span>
      <span className="font-medium text-gray-800">{ksh(amount)}</span>
    </div>
    <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{ width: `${share}%`, background: color }}
      />
    </div>
    <p className="text-xs text-gray-400 mt-1">
      {fmt(count)} fines · {share}%
    </p>
  </div>
);

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-sm">
      <p className="font-medium text-gray-700 mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.name} style={{ color: p.color ?? p.fill }}>
          {p.name}: <span className="font-semibold">{ksh(p.value)}</span>
        </p>
      ))}
    </div>
  );
};

// ─── Main ────────────────────────────────────────────────────────────────────
const MetricsDashboard = () => {
  const [fromDate, setFromDate] = useState<Date>(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), now.getDate());
  });
  const [toDate, setToDate] = useState<Date>(new Date());
  const { mutateAsync, isPending } = useGetPaymentsAnalytics();
  const [data, setData] = useState<PaymentsAnalyticsResponse[]>([]);
  const componentRef = useRef<HTMLDivElement>(null);

  const fetchData = async () => {
    try {
      const res: PaymentsAnalyticsResponse[] = await mutateAsync({
        fromDate: moment(fromDate).format('YYYY-MM-DDTHH:mm'),
        toDate: moment(toDate).format('YYYY-MM-DDTHH:mm'),
      });
      setData(res);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDownloadPdf = () => {
    if (!componentRef.current) return;
    html2canvas(componentRef.current).then((canvas) => {
      const img = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const w = pdf.internal.pageSize.getWidth();
      const h = (canvas.height * w) / canvas.width;
      pdf.addImage(img, 'PNG', 0, 0, w, h);
      pdf.save('fines-dashboard.pdf');
    });
  };

  if (isPending) return <DashboardSkeleton />;

  const d = data[0];

  // ── Derived values ──
  const totalCount = d?.finesListAllResult[0]?.[0] ?? 0;
  const totalAmount = d?.finesListAllResult[0]?.[1] ?? 0;
  const paidCount = d?.finesListPaidResult[0]?.[0] ?? 0;
  const paidAmount = d?.finesListPaidResult[0]?.[1] ?? 0;
  const unpaidCount = d?.finesListUnpaidResult[0]?.[0] ?? 0;
  const unpaidAmount = d?.finesListUnpaidResult[0]?.[1] ?? 0;
  const disputedCount = d?.finesListDisputedResult[0]?.[0] ?? 0;

  const arhCount = d?.finesARH[0]?.[0] ?? 0;
  const arhAmount = d?.finesARH[0]?.[1] ?? 0;
  const vitCount = d?.finesVitronics[0]?.[0] ?? 0;
  const vitAmount = d?.finesVitronics[0]?.[1] ?? 0;

  const enforcedCount = d?.finesListEnforcedResult[0]?.[0] ?? 0;
  const enforcedAmount = d?.finesListEnforcedResult[0]?.[1] ?? 0;
  const notEnfCount = d?.finesNotEnforcedResult[0]?.[0] ?? 0;
  const notEnfAmount = d?.finesNotEnforcedResult[0]?.[1] ?? 0;

  const collectionRate = pct(paidAmount, totalAmount);
  const cameraTotal = arhAmount + vitAmount;

  // ── Chart data ──
  const statusPieData = [
    { name: `Paid (${paidCount})`, value: paidCount, fill: '#16a34a' },
    { name: `Unpaid (${unpaidCount})`, value: unpaidCount, fill: '#ea580c' },
    {
      name: `Disputed (${disputedCount})`,
      value: disputedCount,
      fill: '#9ca3af',
    },
  ].filter((x) => x.value > 0);

  const cameraBarData = [
    { name: 'ARH', amount: arhAmount, fill: '#2563eb' },
    { name: 'Vitronics', amount: vitAmount, fill: '#7c3aed' },
  ];

  const notiTimeline = (d?.finesListPaidViaNoti ?? []).map((e) => ({
    date: e[0] as string,
    amount: (e[2] as number) ?? 0,
  }));

  const enforcementBarData = [
    { name: 'Enforced', amount: enforcedAmount, fill: '#16a34a' },
    { name: 'Not enforced', amount: notEnfAmount, fill: '#dc2626' },
  ];

  return (
    <div className="p-6 space-y-5">
      {/* ── Filter bar ──────────────────────────────────────── */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-wrap items-end gap-3">
          {[
            { id: 'fromDate', label: 'From', val: fromDate, set: setFromDate },
            { id: 'toDate', label: 'To', val: toDate, set: setToDate },
          ].map(({ id, label, val, set }) => (
            <div key={id}>
              <label htmlFor={id} className="block text-xs text-gray-500 mb-1">
                {label}
              </label>
              <div className="relative">
                <input
                  type="datetime-local"
                  id={id}
                  value={formatDateForInput(val)}
                  onChange={(e) => set(new Date(e.target.value))}
                  className="block w-44 px-3 py-2 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                />
                <Calendar className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
              </div>
            </div>
          ))}
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
            style={{ height: 42 }}
          >
            Apply
          </button>
        </div>
        <button
          onClick={handleDownloadPdf}
          className="flex items-center gap-1.5 px-4 py-2 bg-white border border-gray-200 text-sm font-medium text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
          style={{ height: 42 }}
        >
          <Download size={14} /> Export PDF
        </button>
      </div>

      <div ref={componentRef} className="space-y-5">
        {/* ── Top metric cards ─────────────────────────────── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            label="Total fines"
            value={ksh(totalAmount)}
            sub={`${fmt(totalCount)} fines raised`}
          />
          <MetricCard
            label="Collected"
            value={ksh(paidAmount)}
            sub={`${fmt(paidCount)} paid · ${collectionRate}% rate`}
            valueColor="text-green-600"
          />
          <MetricCard
            label="Outstanding"
            value={ksh(unpaidAmount)}
            sub={`${fmt(unpaidCount)} unpaid fines`}
            valueColor="text-orange-600"
          />
          <MetricCard
            label="Not enforced"
            value={ksh(notEnfAmount)}
            sub={`${fmt(notEnfCount)} fines pending`}
            valueColor="text-red-600"
          />
        </div>

        {/* ── Collection rate + Fine status ───────────────── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <SectionLabel>Collection rate</SectionLabel>
            <div className="flex items-center gap-5 mb-5">
              <div
                className="relative flex-shrink-0"
                style={{ width: 80, height: 80 }}
              >
                <svg viewBox="0 0 80 80" width="80" height="80">
                  <circle
                    cx="40"
                    cy="40"
                    r="32"
                    fill="none"
                    stroke="#f3f4f6"
                    strokeWidth="7"
                  />
                  <circle
                    cx="40"
                    cy="40"
                    r="32"
                    fill="none"
                    stroke="#16a34a"
                    strokeWidth="7"
                    strokeLinecap="round"
                    strokeDasharray={`${2 * Math.PI * 32}`}
                    strokeDashoffset={`${2 * Math.PI * 32 * (1 - collectionRate / 100)}`}
                    transform="rotate(-90 40 40)"
                  />
                </svg>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="text-sm font-semibold text-green-600">
                    {collectionRate}%
                  </span>
                </div>
              </div>
              <div className="text-sm text-gray-500">
                <span className="font-semibold text-gray-800">
                  {ksh(paidAmount)}
                </span>{' '}
                collected
                <br />
                of{' '}
                <span className="font-semibold text-gray-800">
                  {ksh(totalAmount)}
                </span>{' '}
                total
              </div>
            </div>
            <div className="space-y-3 border-t border-gray-100 pt-4">
              <ProgressRow
                label="Paid"
                amount={paidAmount}
                count={paidCount}
                share={pct(paidAmount, totalAmount)}
                color="#16a34a"
              />
              <ProgressRow
                label="Unpaid"
                amount={unpaidAmount}
                count={unpaidCount}
                share={pct(unpaidAmount, totalAmount)}
                color="#ea580c"
              />
              <ProgressRow
                label="Disputed"
                amount={0}
                count={disputedCount}
                share={0}
                color="#9ca3af"
              />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
            <SectionLabel>Fine status breakdown</SectionLabel>
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie
                  data={statusPieData}
                  cx="50%"
                  cy="45%"
                  innerRadius={60}
                  outerRadius={85}
                  paddingAngle={3}
                  dataKey="value"
                >
                  {statusPieData.map((entry, i) => (
                    <Cell
                      key={i}
                      fill={entry.fill}
                      stroke="white"
                      strokeWidth={2}
                    />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend
                  iconType="square"
                  iconSize={10}
                  formatter={(v) => (
                    <span style={{ fontSize: 12, color: '#6b7280' }}>{v}</span>
                  )}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* ── Camera revenue ──────────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <SectionLabel>Revenue by camera</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart
                data={cameraBarData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="amount"
                  name="Revenue"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={52}
                >
                  {cameraBarData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="space-y-4">
              {[
                {
                  label: 'ARH',
                  amount: arhAmount,
                  count: arhCount,
                  color: '#2563eb',
                },
                {
                  label: 'Vitronics',
                  amount: vitAmount,
                  count: vitCount,
                  color: '#7c3aed',
                },
              ].map((cam) => (
                <div key={cam.label}>
                  <div className="flex justify-between text-sm mb-1.5">
                    <span className="text-gray-500 flex items-center gap-2">
                      <span
                        className="w-2.5 h-2.5 rounded-sm inline-block"
                        style={{ background: cam.color }}
                      />
                      {cam.label}
                    </span>
                    <span className="font-medium text-gray-800">
                      {ksh(cam.amount)}
                    </span>
                  </div>
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct(cam.amount, cameraTotal || 1)}%`,
                        background: cam.color,
                      }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {fmt(cam.count)} fines · {pct(cam.amount, cameraTotal || 1)}
                    %
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* ── Noti payment timeline ───────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-1">
            <SectionLabel>Noti payment activity</SectionLabel>
            <span className="text-xs px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">
              {notiTimeline.length} transaction
              {notiTimeline.length !== 1 ? 's' : ''} · via Noti
            </span>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Only active payment channel in this period
          </p>
          {notiTimeline.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <BarChart
                data={notiTimeline}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickFormatter={(v) => `KSh ${v}`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="amount"
                  name="Noti payment"
                  fill="#2563eb"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={56}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-gray-400 py-8 text-center">
              No Noti payments in this period
            </p>
          )}
        </div>

        {/* ── Enforcement breakdown ────────────────────────── */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
          <SectionLabel>Enforcement status</SectionLabel>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-center">
            <div className="space-y-4">
              {[
                {
                  label: 'Enforced',
                  amount: enforcedAmount,
                  count: enforcedCount,
                  color: '#16a34a',
                },
                {
                  label: 'Not enforced',
                  amount: notEnfAmount,
                  count: notEnfCount,
                  color: '#dc2626',
                },
              ].map((item) => {
                const total = enforcedAmount + notEnfAmount;
                return (
                  <div key={item.label}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-500">{item.label}</span>
                      <span className="font-medium text-gray-800">
                        {ksh(item.amount)}
                      </span>
                    </div>
                    <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${pct(item.amount, total || 1)}%`,
                          background: item.color,
                        }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {fmt(item.count)} fines · {pct(item.amount, total || 1)}%
                    </p>
                  </div>
                );
              })}
            </div>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart
                data={enforcementBarData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 12, fill: '#9ca3af' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 11, fill: '#9ca3af' }}
                  tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="amount"
                  name="Amount"
                  radius={[4, 4, 0, 0]}
                  maxBarSize={52}
                >
                  {enforcementBarData.map((entry, i) => (
                    <Cell key={i} fill={entry.fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MetricsDashboard;
